import { useCallback, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Download,
  FileUp,
  Loader2,
  Upload,
} from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/marketing/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/marketing/components/ui/table";
import { Badge } from "@/marketing/components/ui/badge";
import { Button } from "@/marketing/components/ui/button";
import {
  parseCampaignCsv,
  type ParseCampaignCsvResult,
} from "@/marketing/lib/campaignCsvParser";
import { extractErrorMessage } from "@/marketing/lib/errorMessage";
import { campaignPipelineAPIClient } from "@/marketing/infrastructure/influencer/campaign/pipeline/campaignPipelineAPIClient";
import { CAMPAIGN_PIPELINE_KEYS } from "@/marketing/infrastructure/influencer/campaign/pipeline/utils/keys";
import { useAuthStore } from "@/marketing/stores/useAuthStore";
import type { BulkImportCampaignInfluencersResponse } from "@/marketing/models/campaignInfluencer";
import type { CampaignPipelineDefinition } from "@/marketing/models/campaignPipeline";

type Step = "upload" | "preview" | "result";

interface CampaignCSVImportDialogProps {
  campaignId: string;
  pipelineDefinition: CampaignPipelineDefinition;
}

const TEMPLATE_HEADER =
  "handle,platform,stage,settlement_amount,notes,selected_skus,email";

function buildTemplateExamples(
  pipelineDefinition: CampaignPipelineDefinition,
): string[] {
  const [firstStage, secondStage = firstStage] = pipelineDefinition.stages;
  return [
    `@example_user,tiktok,${firstStage?.stage ?? ""},,"Initial outreach",,creator@example.com`,
    `another_user,tiktok,${secondStage?.stage ?? ""},300,Confirmed via DM,1000572314,another@example.com`,
  ];
}

export function CampaignCSVImportDialog({
  campaignId,
  pipelineDefinition,
}: CampaignCSVImportDialogProps) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>("upload");
  const [parsed, setParsed] = useState<ParseCampaignCsvResult>({
    rows: [],
    errors: [],
  });
  const [result, setResult] =
    useState<BulkImportCampaignInfluencersResponse | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const queryClient = useQueryClient();
  const importedBy =
    useAuthStore((s) => s.user?.email) ?? "csv-import";

  const importMutation = useMutation({
    mutationFn: () =>
      campaignPipelineAPIClient.bulkImportCampaignInfluencers(campaignId, {
        rows: parsed.rows,
        importedBy,
      }),
    onSuccess: (data) => {
      setResult(data);
      setStep("result");
      void queryClient.invalidateQueries({
        queryKey: CAMPAIGN_PIPELINE_KEYS.lists(),
      });
      toast.success(`Imported ${data.imported} influencer(s)`);
    },
    onError: (err: unknown) =>
      toast.error(extractErrorMessage(err, "Bulk import failed")),
  });

  const reset = () => {
    setStep("upload");
    setParsed({ rows: [], errors: [] });
    setResult(null);
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) reset();
  };

  const downloadTemplate = () => {
    const csv = `${TEMPLATE_HEADER}\n${buildTemplateExamples(pipelineDefinition).join("\n")}\n`;
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "campaign_influencer_import_template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const processFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result;
      if (typeof content !== "string") {
        toast.error("Failed to read file content");
        return;
      }
      setParsed(parseCampaignCsv(content, pipelineDefinition));
      setStep("preview");
    };
    reader.onerror = () => toast.error("Failed to read file");
    reader.readAsText(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.name.endsWith(".csv")) processFile(file);
    else toast.error("Please drop a .csv file");
  }, []);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="tw-gap-1.5">
          <Upload className="tw-h-3.5 tw-w-3.5" />
          Import CSV
        </Button>
      </DialogTrigger>
      <DialogContent className="tw-flex tw-max-h-[85vh] tw-flex-col tw-gap-0 tw-overflow-hidden tw-p-0 sm:tw-max-w-2xl">
        <DialogHeader className="tw-shrink-0 tw-border-b tw-p-6 tw-pb-4">
          <DialogTitle>Import Influencers from CSV</DialogTitle>
          <DialogDescription>
            Upload a CSV with columns: handle, platform, stage (
            {pipelineDefinition.stages.map((s) => s.stage).join(", ")}),
            settlement_amount (optional), notes (optional), selected_skus
            (required for choice-reward campaigns, separate multiple SKUs with
            &quot;;&quot;), email (optional, links the creator&apos;s hub account
            to this campaign). Missing influencers will be auto-created in the
            master pool.
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-6">
            <div
              className={`tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-lg tw-border-2 tw-border-dashed tw-p-12 tw-transition-colors ${
                isDragOver ? "tw-border-blue-500 tw-bg-blue-50" : "tw-border-muted"
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={() => setIsDragOver(false)}
              onDrop={handleDrop}
            >
              <FileUp className="tw-mb-3 tw-h-10 tw-w-10 tw-text-muted-foreground" />
              <p className="tw-mb-1 tw-text-sm tw-font-medium">
                Drop a CSV file here, or click to browse
              </p>
              <p className="tw-mb-4 tw-text-xs tw-text-muted-foreground">
                Supports .csv files
              </p>
              <div className="tw-flex tw-items-center tw-gap-3">
                <label className="tw-cursor-pointer tw-rounded-md tw-bg-[#2563EB] tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700">
                  Choose File
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="tw-hidden"
                  />
                </label>
                <button
                  type="button"
                  onClick={downloadTemplate}
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-text-muted-foreground hover:tw-text-foreground"
                >
                  <Download className="tw-h-3.5 tw-w-3.5" />
                  Download Template
                </button>
              </div>
            </div>
          </div>
        )}

        {step === "preview" && (
          <>
            <div className="tw-min-h-0 tw-flex-1 tw-space-y-4 tw-overflow-y-auto tw-p-6">
              <div className="tw-flex tw-items-center tw-gap-2">
                <Badge variant="secondary">
                  {parsed.rows.length} rows parsed
                </Badge>
                {parsed.errors.length > 0 && (
                  <Badge variant="destructive">
                    {parsed.errors.length} errors
                  </Badge>
                )}
              </div>

              {parsed.errors.length > 0 && (
                <div className="tw-rounded-md tw-border tw-border-red-200 tw-bg-red-50 tw-p-3">
                  <div className="tw-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-medium tw-text-red-800">
                    <AlertCircle className="tw-h-4 tw-w-4" />
                    Parse warnings
                  </div>
                  <ul className="tw-mt-1 tw-list-inside tw-list-disc tw-text-xs tw-text-red-700">
                    {parsed.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                    {parsed.errors.length > 5 && (
                      <li>...and {parsed.errors.length - 5} more</li>
                    )}
                  </ul>
                </div>
              )}

              {parsed.rows.length > 0 && (
                <div className="tw-rounded-md tw-border">
                  <Table>
                    <TableHeader>
                      <TableRow className="tw-bg-neutral-50">
                        <TableHead>Handle</TableHead>
                        <TableHead>Platform</TableHead>
                        <TableHead>Stage</TableHead>
                        <TableHead>Settlement</TableHead>
                        <TableHead>Products</TableHead>
                        <TableHead>Email</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsed.rows.slice(0, 10).map((row, i) => (
                        <TableRow key={i}>
                          <TableCell className="tw-text-sm">
                            @{row.handle}
                          </TableCell>
                          <TableCell className="tw-text-sm">
                            {row.platform}
                          </TableCell>
                          <TableCell className="tw-text-sm">{row.stage}</TableCell>
                          <TableCell className="tw-text-sm">
                            {row.settlementAmount !== undefined
                              ? `$${row.settlementAmount}`
                              : "—"}
                          </TableCell>
                          <TableCell className="tw-text-sm">
                            {row.selectedSkus?.join(", ") ?? "—"}
                          </TableCell>
                          <TableCell className="tw-max-w-[160px] tw-truncate tw-text-sm">
                            {row.email ?? "—"}
                          </TableCell>
                          <TableCell className="tw-max-w-[180px] tw-truncate tw-text-sm">
                            {row.notes ?? "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {parsed.rows.length > 10 && (
                    <p className="tw-px-3 tw-py-2 tw-text-xs tw-text-muted-foreground">
                      Showing 10 of {parsed.rows.length} rows
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="tw-flex tw-shrink-0 tw-justify-end tw-gap-2 tw-border-t tw-bg-background tw-p-4">
              <Button variant="outline" size="sm" onClick={reset}>
                Back
              </Button>
              <Button
                size="sm"
                onClick={() => importMutation.mutate()}
                disabled={
                  parsed.rows.length === 0 || importMutation.isPending
                }
                className="tw-gap-1.5"
              >
                {importMutation.isPending && (
                  <Loader2 className="tw-h-4 tw-w-4 tw-animate-spin" />
                )}
                Import {parsed.rows.length} influencer(s)
              </Button>
            </div>
          </>
        )}

        {step === "result" && result && (
          <>
            <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-6">
              <div className="tw-rounded-md tw-border tw-border-green-200 tw-bg-green-50 tw-p-4">
                <p className="tw-text-sm tw-font-medium tw-text-green-800">
                  Import complete
                </p>
                <div className="tw-mt-2 tw-space-y-1 tw-text-sm tw-text-green-700">
                  <p>Enrolled: {result.imported}</p>
                  <p>Skipped (already in campaign): {result.skipped}</p>
                  <p>Created in master pool: {result.createdInMaster}</p>
                  {result.errors.length > 0 && (
                    <p className="tw-text-red-600">
                      Partial errors: {result.errors.length}
                    </p>
                  )}
                </div>
                {result.errors.length > 0 && (
                  <ul className="tw-mt-2 tw-list-inside tw-list-disc tw-text-xs tw-text-red-700">
                    {result.errors.slice(0, 5).map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="tw-flex tw-shrink-0 tw-justify-end tw-border-t tw-bg-background tw-p-4">
              <Button size="sm" onClick={() => handleOpenChange(false)}>
                Done
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

